import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { Fragment as external_react_Fragment, cloneElement, forwardRef, useEffect, useRef, useState } from "react";
import { useCombinedRefs } from "../../hooks/useCombinedRefs.js";
import { useDevelopmentEffect } from "../../hooks/_internal/useDevelopmentEffect.js";
import { useFocus } from "../../hooks/useFocus.js";
import { useHover } from "../../hooks/useHover.js";
import { useUUID } from "../../hooks/useUUID.js";
import { useOnEscape } from "../../hooks/_internal/useOnEscape.js";
import Tooltip_module from "./Tooltip.module.js";
import { FloatingContainer } from "../floatingContainer/FloatingContainer.js";
import { combineClasses } from "../../utilities/combineClasses.js";
const Tooltip = /*#__PURE__*/ forwardRef(({ text, className, active = true, attachAs = "description", children, ...props }, ref)=>{
    const id = useUUID({
        prefix: 'tooltip'
    });
    const anchorRef = useRef(null);
    const [anchorHoverRef, isAnchorHovered] = useHover({
        delay: {
            hover: 300,
            unhover: 100
        }
    });
    const [tooltipHoverRef, isTooltipHovered] = useHover();
    const [anchorFocusRef, isAnchorFocused] = useFocus();
    const [isAnchorTriggered, setIsAnchorTriggered] = useState(false);
    const isExpanded = isAnchorTriggered || isTooltipHovered;
    const combinedAnchorRef = useCombinedRefs(children.ref, anchorRef, anchorHoverRef, anchorFocusRef);
    const combinedTooltipRef = useCombinedRefs(tooltipHoverRef, ref);
    useEffect(()=>{
        setIsAnchorTriggered(isAnchorFocused);
    }, [
        isAnchorFocused
    ]);
    useEffect(()=>{
        setIsAnchorTriggered(isAnchorHovered);
    }, [
        isAnchorHovered
    ]);
    useOnEscape(()=>setIsAnchorTriggered(false), {
        element: document
    });
    useDevelopmentEffect(()=>{
        if (children.type === external_react_Fragment) console.error('[Tooltip] The child must not be a fragment as it must be able to hold a reference and handle aria-* attributes.');
    }, [
        children
    ]);
    if (!active || !text) return children;
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ cloneElement(children, {
                ref: combinedAnchorRef,
                'aria-describedby': "description" === attachAs ? [
                    id,
                    children.props['aria-describedby']
                ].filter(Boolean).join(' ') : void 0,
                'aria-labelledby': 'label' === attachAs ? [
                    id,
                    children.props['aria-labelledby']
                ].filter(Boolean).join(' ') : void 0
            }),
            /*#__PURE__*/ jsx(FloatingContainer, {
                unmountOnExit: false,
                arrow: true,
                portal: true,
                open: isExpanded,
                placement: "top",
                shift: true,
                anchorRef: anchorRef,
                ref: combinedTooltipRef,
                role: "description" === attachAs ? 'tooltip' : void 0,
                id: id,
                className: combineClasses(className, Tooltip_module.tooltip),
                contentProps: {
                    className: Tooltip_module.tooltipContent
                },
                "aria-hidden": !isExpanded || 'visual only' === attachAs,
                ...props,
                children: text
            })
        ]
    });
});
Tooltip.displayName = 'Tooltip';
export { Tooltip };
